<template>
  <div class="data-management well-page qaqc-overview-page">
    <QAQCOverviewShimmer v-if="loading" />
    <UnifiedQAQCDashboard
      v-else-if="mappedDashboard"
      :summary-stats="summaryStats"
      :qc-status-overview="qcStatusOverview"
      :qc-status-panel="qcStatusPanel"
      :active-test-tab="activeTestTab"
      :tab-loading="tabLoading"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :distribution-plot-options="distributionPlotOptions"
      :selected-plot-id="selectedPlotId"
      :distribution-data="distributionData"
      :sample-ids="sampleIds"
      :selected-sample-ids="selectedSampleIds"
      :consistency-cross-test-rows="consistencyCrossTestRows"
      :consistency-ordering-rows="consistencyOrderingRows"
      :equilibrium-plot-rows="equilibriumPlotRows"
      :dl-material-balance-plot-options="dlMaterialBalancePlotOptions"
      :selected-material-balance-plot-id="selectedMaterialBalancePlotId"
      :dl-material-balance-distribution-data="dlMaterialBalanceDistributionData"
      :dl-feed-deviation-data="dlFeedDeviationData"
      @update:active-test-tab="onActiveTestTabChange"
      @update:selected-plot-id="selectedPlotId = $event"
      @update:selected-material-balance-plot-id="selectedMaterialBalancePlotId = $event"
      @update:selected-sample-ids="selectedSampleIds = $event"
    />
    <div v-else-if="!loading && error" class="tabs-no-test-div timeout">
      <div class="text-center">
        {{ error }}
        <v-icon flat @click="fetchOverviewData()" color="blue-darken-3" icon="mdi-reload" size="large"></v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import UnifiedQAQCDashboard from "@/components/layout/QAQC/Overview/UnifiedQAQCDashboard.vue";
import QAQCOverviewShimmer from "@/components/layout/QAQC/Overview/QAQCOverviewShimmer.vue";
import {
  fetchQaqcDashboardTab,
  mergeDashboardApiData,
  mapDashboardTabToView,
  mapDashboardToOverview,
} from "./qaqcDashboardApi.js";
import {
  mapDistributionPlotToViewData,
  emptyDistributionViewData,
  DL_MATERIAL_BALANCE_PLOT_ORDER,
  DL_FEED_DEVIATION_PLOT_NAMES,
} from "@/components/layout/QAQC/Overview/distributionPlotMapper.js";
import {
  isValidQaqcOverviewTab,
  parseQaqcOverviewTabFromQuery,
  QAQC_OVERVIEW_TAB_DL,
  QAQC_OVERVIEW_TAB_GAS_CCE,
  QAQC_OVERVIEW_TAB_OIL_CCE,
} from "@/components/layout/QAQC/Overview/qaqcOverviewTab.js";

const OVERVIEW_TAB_FETCH_ORDER = [
  QAQC_OVERVIEW_TAB_OIL_CCE,
  QAQC_OVERVIEW_TAB_GAS_CCE,
  QAQC_OVERVIEW_TAB_DL,
];

function plotNameMatches(plot, selectedId) {
  const a = String(plot?.Name ?? plot?.name ?? "").trim();
  const b = String(selectedId ?? "").trim();
  return a !== "" && a === b;
}

function distPlotToData(plot, selectedId) {
  if (!plot || !plotNameMatches(plot, selectedId)) return null;
  return mapDistributionPlotToViewData(plot);
}

export default {
  name: "QAQCOverview",
  components: {
    UnifiedQAQCDashboard,
    QAQCOverviewShimmer,
  },
  data() {
    return {
      loading: false,
      error: null,
      tabRawData: {},
      tabFetchPromises: {},
      mergedApiData: null,
      activeFetchId: 0,
      dataSessionId: 0,
      activeTestTab: QAQC_OVERVIEW_TAB_OIL_CCE,
      selectedPlotId: "",
      selectedMaterialBalancePlotId: "",
      selectedSampleIds: [],
      tabLoading: false,
    };
  },
  computed: {
    mappedDashboard() {
      if (!this.mergedApiData) return null;
      return mapDashboardToOverview(this.mergedApiData);
    },
    summaryStats() {
      return this.mappedDashboard?.summaryStats || this.defaultSummaryStats();
    },
    qcStatusOverview() {
      return this.mappedDashboard?.qcStatusOverview || this.defaultQcStatusOverview();
    },
    qcStatusPanel() {
      return this.mappedDashboard?.qcStatusPanel || this.defaultQcStatusPanel();
    },
    tabData() {
      const raw = this.tabRawData[this.activeTestTab];
      if (!raw) return {};
      return mapDashboardTabToView(this.activeTestTab, raw) || {};
    },
    scorecardRows() {
      return this.tabData.scorecardRows || [];
    },
    scorecardSummary() {
      return this.tabData.scorecardSummary || this.defaultScorecardSummary();
    },
    validationMatrixRows() {
      return this.tabData.validationMatrixRows || [];
    },
    validationMatrixTitle() {
      return this.tabData.validationMatrixTitle || "Validation Pass Rate Matrix";
    },
    validationMatrixDescription() {
      return this.tabData.validationMatrixDescription || "Percentage of all samples passing each validation check";
    },
    consistencyCrossTestRows() {
      return this.tabData.consistencyCrossTestRows || [];
    },
    consistencyOrderingRows() {
      return this.tabData.consistencyOrderingRows || [];
    },
    equilibriumPlotRows() {
      return this.tabData.equilibriumPlotRows || [];
    },
    distributionPlotOptions() {
      return this.tabData.distributionPlotOptions || [];
    },
    distributionData() {
      const plots = this.tabData.distributionPlots || [];
      const firstId = this.distributionPlotOptions[0]?.id;
      const plotId = this.selectedPlotId || firstId;
      if (!plotId || !plots.length) return this.defaultDistributionData();
      const plot = plots.find((p) => plotNameMatches(p, plotId));
      const data = plot ? distPlotToData(plot, plotId) : null;
      return data || this.defaultDistributionData();
    },
    dlDistributionPlots() {
      return this.tabData.distributionPlots || [];
    },
    dlMaterialBalancePlotOptions() {
      if (this.activeTestTab !== "dlTests") return [];
      const plots = this.dlDistributionPlots;
      return DL_MATERIAL_BALANCE_PLOT_ORDER.map((id) => plots.find((p) => plotNameMatches(p, id)))
        .filter(Boolean)
        .map((p) => ({ id: p.Name, label: p.DisplayName || p.Name }));
    },
    dlMaterialBalanceDistributionData() {
      if (this.activeTestTab !== "dlTests") return emptyDistributionViewData();
      const id = this.selectedMaterialBalancePlotId;
      if (!id) return emptyDistributionViewData();
      const plot = this.dlDistributionPlots.find((p) => plotNameMatches(p, id));
      return plot ? mapDistributionPlotToViewData(plot) : emptyDistributionViewData();
    },
    dlFeedDeviationData() {
      if (this.activeTestTab !== "dlTests") return emptyDistributionViewData();
      const plots = this.dlDistributionPlots;
      const plot = DL_FEED_DEVIATION_PLOT_NAMES.map((n) => plots.find((p) => plotNameMatches(p, n))).find(Boolean);
      return plot ? mapDistributionPlotToViewData(plot) : emptyDistributionViewData();
    },
    sampleIds() {
      return this.mappedDashboard?.sampleIds || [];
    },
  },
  watch: {
    "$route.query.qaqcOverviewTab": {
      handler(tab) {
        if (isValidQaqcOverviewTab(tab) && tab !== this.activeTestTab) {
          this.onActiveTestTabChange(tab);
        }
      },
    },
    distributionPlotOptions: {
      immediate: true,
      handler(opts) {
        if (!opts.length) return;
        const hasCurrent = opts.some((o) => o.id === this.selectedPlotId);
        if (!hasCurrent || !this.selectedPlotId) this.selectedPlotId = opts[0].id;
      },
    },
    dlMaterialBalancePlotOptions: {
      immediate: true,
      handler(opts) {
        if (this.activeTestTab !== "dlTests") return;
        if (!opts.length) {
          this.selectedMaterialBalancePlotId = "";
          return;
        }
        const has = opts.some((o) => o.id === this.selectedMaterialBalancePlotId);
        if (!has) this.selectedMaterialBalancePlotId = opts[0].id;
      },
    },
  },
  mounted() {
    const tab = parseQaqcOverviewTabFromQuery(this.$route.query);
    if (tab) this.activeTestTab = tab;
    this.syncOverviewTabToRoute(this.activeTestTab);
    this.fetchOverviewData();
  },
  methods: {
    onActiveTestTabChange(tab) {
      if (!isValidQaqcOverviewTab(tab) || tab === this.activeTestTab) return;
      this.activeTestTab = tab;
      this.syncOverviewTabToRoute(tab);
      this.resetTabPlotSelection();
      this.fetchTabData(tab);
    },
    resetTabPlotSelection() {
      this.selectedPlotId = "";
      this.selectedMaterialBalancePlotId = "";
    },
    syncOverviewTabToRoute(tab) {
      if (!isValidQaqcOverviewTab(tab)) return;
      const current = this.$route.query?.qaqcOverviewTab;
      if (current === tab) return;
      this.$router.replace({
        query: { ...this.$route.query, qaqcOverviewTab: tab },
      });
    },
    defaultSummaryStats() {
      return {
        totalTests: "-",
        totalTestsChange: "",
        cceTests: "-",
        cceTestsPercent: "",
        dlTests: "-",
        dlTestsPercent: "",
        passRate: "-",
      };
    },
    defaultQcStatusOverview() {
      return {
        categories: ["Passed", "Failed", "Needs Review"],
        series: [
          { name: "CCE Tests", color: "#2298D5", values: [0, 0, 0] },
          { name: "DL Tests", color: "#F97316", values: [0, 0, 0] },
        ],
      };
    },
    defaultQcStatusPanel() {
      return {
        passed: { count: "-", total: "-", percent: "-" },
        needsReview: { count: "-", total: "-", percent: "-" },
        failed: { count: "-", total: "-", percent: "-" },
        subtitle: "Current QC validation results",
      };
    },
    defaultScorecardSummary() {
      return {
        overallPassRate: "-",
        passedQc: "-",
        pendingQc: "-",
        failed: "-",
      };
    },
    defaultDistributionData() {
      return emptyDistributionViewData();
    },
    rebuildMergedApiData() {
      let merged = null;
      for (const tab of OVERVIEW_TAB_FETCH_ORDER) {
        const data = this.tabRawData[tab];
        if (data) merged = mergeDashboardApiData(merged, data, tab);
      }
      this.mergedApiData = merged;
    },
    ensureTabFetch(tab) {
      if (this.tabRawData[tab]) return Promise.resolve(this.tabRawData[tab]);
      if (this.tabFetchPromises[tab]) return this.tabFetchPromises[tab];
      const sessionId = this.dataSessionId;
      const promise = fetchQaqcDashboardTab(tab)
        .then((data) => {
          if (sessionId !== this.dataSessionId) return data;
          this.tabRawData = { ...this.tabRawData, [tab]: data };
          this.rebuildMergedApiData();
          return data;
        })
        .finally(() => {
          if (sessionId === this.dataSessionId) {
            delete this.tabFetchPromises[tab];
          }
        });
      this.tabFetchPromises[tab] = promise;
      return promise;
    },
    // Each tab endpoint only returns real Passed/Failed/Pending counts for its own
    // test section (other sections come back zeroed), so the remaining tabs are
    // prefetched in the background to complete the overall chart and QC status stats.
    prefetchOtherTabs() {
      OVERVIEW_TAB_FETCH_ORDER.filter((t) => t !== this.activeTestTab).forEach((tab) => {
        this.ensureTabFetch(tab).catch(() => {});
      });
    },
    fetchTabData(tab) {
      if (!isValidQaqcOverviewTab(tab)) return;
      if (this.tabRawData[tab]) {
        this.activeFetchId += 1;
        this.rebuildMergedApiData();
        this.loading = false;
        this.tabLoading = false;
        this.error = null;
        this.prefetchOtherTabs();
        return;
      }

      const fetchId = ++this.activeFetchId;
      const isInitialLoad = !this.mergedApiData;
      if (isInitialLoad) this.loading = true;
      this.tabLoading = true;
      this.error = null;

      this.ensureTabFetch(tab)
        .catch((err) => {
          if (fetchId !== this.activeFetchId) return;
          this.error = err?.message || "Failed to load QA/QC dashboard";
        })
        .finally(() => {
          if (fetchId !== this.activeFetchId) return;
          this.loading = false;
          this.tabLoading = false;
        });
      this.prefetchOtherTabs();
    },
    fetchOverviewData() {
      this.dataSessionId += 1;
      this.tabRawData = {};
      this.tabFetchPromises = {};
      this.mergedApiData = null;
      this.activeFetchId += 1;
      this.resetTabPlotSelection();
      this.fetchTabData(this.activeTestTab);
    },
  },
};
</script>

<style scoped>
@import "../../assets/Styling/qaqc.css";
</style>
